 
import { Component, OnDestroy, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { User } from '../../models/auth.model';
import { NotificationBellComponent } from '../notification-bell/notification-bell';
import { UiService } from '../../services/ui.service';
import { SearchService } from '../../services/search.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, NotificationBellComponent],
  templateUrl: './header.html',
  styleUrls: ['./header.scss'],
})
export class Header implements OnInit, OnDestroy {
  showProfileMenu = false;
  currentUser: User | null = null;
  isAuthenticated = false;
  currentLanguage: 'en' | 'ar' = 'en';
  currentDirection: 'ltr' | 'rtl' = 'ltr';
  showSearch = false;
  @ViewChild('profileMenuWrapper') profileMenuWrapper: ElementRef | undefined;

  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router
    , private uiService: UiService
    , private searchService: SearchService
  ) {}

  ngOnInit(): void {
    // Initialize language
    this.currentLanguage = (this.translate.currentLang || 'en') as 'en' | 'ar';
    this.currentDirection = this.currentLanguage === 'ar' ? 'rtl' : 'ltr';

    // Subscribe to auth state and init notifications when authenticated
    this.authService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isAuthenticated = state.isAuthenticated;
        this.currentUser = state.currentUser;

        if (state.isAuthenticated && state.currentUser?.email) {
          this.notificationService.initializeConnection(state.currentUser.email);
        }
      });

    // Show search only on file-management route
    this.showSearch = this.router.url?.startsWith('/file-management');
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(evt => {
      if (evt instanceof NavigationEnd) {
        this.showSearch = evt.urlAfterRedirects.startsWith('/file-management');
      }
    });
  }
 toggleLanguage(): void {
    this.currentLanguage = this.currentLanguage === 'en' ? 'ar' : 'en';
    this.currentDirection = this.currentLanguage === 'ar' ? 'rtl' : 'ltr';
    this.translate.use(this.currentLanguage);
    localStorage.setItem('language', this.currentLanguage);
    document.documentElement.lang = this.currentLanguage;
    document.documentElement.dir = this.currentDirection;
    this.router.navigate(['/dashboard']);
  }
  toggleProfileMenu(): void {
    this.showProfileMenu = !this.showProfileMenu;
  }

  /**
   * Close profile menu when clicking outside of it
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.profileMenuWrapper && !this.profileMenuWrapper.nativeElement.contains(event.target)) {
      this.showProfileMenu = false;
    }
  }

  logout(): void {
    if (this.currentUser?.iamUserId) {
      this.notificationService.unsubscribeFromNotifications(this.currentUser.iamUserId.toString());
      this.notificationService.stopConnection();
    }
    this.authService.logout();
    this.showProfileMenu = false;
  }

  getInitials(): string {
    if (!this.currentUser) return 'U';
    const first = this.currentUser.firstName?.charAt(0) || '';
    const last = this.currentUser.lastName?.charAt(0) || '';
    return (first + last || 'U').toUpperCase();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void {
    this.uiService.toggleSidebar();
  }

  onSearchInput(term: string): void {
    // forward search term to consumers
    this.searchService.setSearch(term || '');
  }
}
